export type RewardRarity = 'common' | 'rare' | 'epic' | 'legendary' | 'mythic';

export type RewardKind = 'feature' | 'font' | 'background' | 'headline' | 'fortune' | 'gems' | 'ak47';

const shopCostByRarity: Record<RewardRarity, number> = {
  common: 30,
  rare: 60,
  epic: 110,
  legendary: 180,
  mythic: 300,
};

const duplicateGemPayoutByRarity: Record<RewardRarity, number> = {
  common: 8,
  rare: 16,
  epic: 32,
  legendary: 64,
  mythic: 120,
};

const comboBonusByRarity: Record<RewardRarity, number> = {
  common: 3,
  rare: 6,
  epic: 12,
  legendary: 24,
  mythic: 50,
};

export interface LootReward {
  id: string;
  label: string;
  kind: RewardKind;
  rarity: RewardRarity;
  weight: number;
  value: string | number;
  description: string;
}

export interface LootTickerItem {
  reward: LootReward;
}

export interface ShopItem {
  reward: LootReward;
  cost: number;
  unlocked: boolean;
  purchasable: boolean;
}

export interface LootComboBonus {
  streak: number;
  gems: number;
  isJackpot: boolean;
}

export interface LootEasingOption {
  id: string;
  label: string;
  css: string;
}

export interface LootAnimationProfile {
  rarity: RewardRarity;
  anticipationMs: number;
  burstMs: number;
  settleMs: number;
  chestLiftPx: number;
  chestShakeDegrees: number;
  burstScale: number;
}

export interface LootSpinPlan {
  reward: LootReward;
  easing: LootEasingOption;
  landingIndex: number;
  finalRotationDegrees: number;
  durationMs: number;
  tickerItems: LootTickerItem[];
  tickerLabels: string[];
  targetTickerIndex: number;
}

export interface LootInventory {
  gems: number;
  openedBoxCount: number;
  ak47Unlocked: boolean;
  unlockedFeatureIds: string[];
  unlockedFontIds: string[];
  unlockedBackgroundIds?: string[];
  unlockedHeadlineIds?: string[];
  equippedFontId?: string;
  equippedBackgroundId?: string;
  equippedHeadlineId?: string;
  lastFortune?: string;
  unlockedAiTipIds: string[];
}

export interface FontOption {
  id: string;
  label: string;
}

export interface FeatureOption {
  id: string;
  label: string;
}

export interface UnlockMenuItem {
  id: string;
  label: string;
  href: string;
  external?: boolean;
}

export const defaultFontOption: FontOption = {
  id: 'default',
  label: 'Default Font',
};


export const lootEasingOptions: LootEasingOption[] = [
  { id: 'neon-snap', label: 'Neon Snap', css: 'cubic-bezier(0.12, 0.82, 0.08, 1)' },
  { id: 'plasma-sling', label: 'Plasma Sling', css: 'cubic-bezier(0.17, 0.89, 0.32, 1.28)' },
  { id: 'gravity-well', label: 'Gravity Well', css: 'cubic-bezier(0.65, 0, 0.35, 1)' },
  { id: 'quantum-pop', label: 'Quantum Pop', css: 'cubic-bezier(0.34, 1.56, 0.64, 1)' },
  { id: 'starlight-drift', label: 'Starlight Drift', css: 'cubic-bezier(0.22, 1, 0.36, 1)' },
  { id: 'arcade-tilt', label: 'Arcade Tilt', css: 'cubic-bezier(0.2, 0.8, 0.2, 1)' },
  { id: 'comet-brake', label: 'Comet Brake', css: 'cubic-bezier(0.11, 0, 0.5, 0)' },
  { id: 'treasure-bounce', label: 'Treasure Bounce', css: 'cubic-bezier(0.68, -0.55, 0.27, 1.55)' },
  { id: 'warp-coast', label: 'Warp Coast', css: 'cubic-bezier(0.16, 1, 0.3, 1)' },
  { id: 'glitch-kick', label: 'Glitch Kick', css: 'cubic-bezier(0.7, 0, 0.84, 0)' },
  { id: 'lunar-ease', label: 'Lunar Ease', css: 'cubic-bezier(0.25, 1, 0.5, 1)' },
  { id: 'jackpot-rush', label: 'Jackpot Rush', css: 'cubic-bezier(0.76, 0, 0.24, 1)' },
];

export const rarityMeta: Record<RewardRarity, { label: string; color: string }> = {
  common: { label: 'Common', color: '#69f7ff' },
  rare: { label: 'Rare', color: '#7cf58a' },
  epic: { label: 'Epic', color: '#bf5fff' },
  legendary: { label: 'Legendary', color: '#ffd166' },
  mythic: { label: 'Mythic', color: '#ff4f4f' },
};

export const featureUnlocks: FeatureOption[] = [
  { id: 'background-animation', label: 'Background Animation' },
  { id: 'gems', label: 'Gem Wallet' },
  { id: 'shop', label: 'Gem Shop' },
  { id: 'global-lighting', label: 'Coloured Global Lighting' },
  { id: 'color-wheel', label: 'Colour Spin Wheel' },
  { id: 'animated-title', label: 'Animated Title Text' },
  { id: 'hero-copy', label: 'Secret Transmission' },
  { id: 'actions', label: 'Action Buttons' },
  { id: 'status-grid', label: 'Status Cards' },
  { id: 'demo-panel', label: 'Signal Portal' },
  { id: 'orbital-ring', label: 'Orbital Ring' },
  { id: 'eyebrow', label: 'Signal Beacon' },
  { id: 'ai-guide', label: 'Steve’s Guide to Working with AI' },
  { id: 'cursor-trail', label: 'Stardust Cursor' },
  { id: 'ufo-pet', label: 'Pocket UFO' },
];

export const lootRewards: LootReward[] = [
  {
    id: 'feature-background-animation',
    label: 'Background Animation',
    kind: 'feature',
    rarity: 'epic',
    weight: 16,
    value: 'background-animation',
    description: 'Switches on the starfield, film grain, and cinematic backdrop.',
  },
  {
    id: 'feature-gems',
    label: 'Gem Wallet',
    kind: 'feature',
    rarity: 'common',
    weight: 14,
    value: 'gems',
    description: 'Adds gem tracking so future boxes can stockpile shiny currency.',
  },
  {
    id: 'feature-shop',
    label: 'Gem Shop',
    kind: 'feature',
    rarity: 'rare',
    weight: 7,
    value: 'shop',
    description: 'Unlocks the standalone gem shop panel for spending shiny currency on missing rewards.',
  },
  {
    id: 'feature-global-lighting',
    label: 'Coloured Global Lighting',
    kind: 'feature',
    rarity: 'rare',
    weight: 10,
    value: 'global-lighting',
    description: 'Primes the page with coloured global lighting before the wheel of many many colours can steer it.',
  },
  {
    id: 'feature-color-wheel',
    label: 'The wheel of many many colours',
    kind: 'feature',
    rarity: 'epic',
    weight: 8,
    value: 'color-wheel',
    description: 'Mounts the corner wheel of many many colours after the global lighting rig is online.',
  },
  {
    id: 'feature-animated-title',
    label: 'Animated Title Text',
    kind: 'feature',
    rarity: 'rare',
    weight: 11,
    value: 'animated-title',
    description: 'Upgrades the plain greeting into the holographic animated headline.',
  },
  {
    id: 'feature-hero-copy',
    label: 'Secret Transmission',
    kind: 'feature',
    rarity: 'common',
    weight: 9,
    value: 'hero-copy',
    description: 'Restores the explanatory homepage copy below the greeting.',
  },
  {
    id: 'feature-actions',
    label: 'Action Buttons',
    kind: 'feature',
    rarity: 'epic',
    weight: 8,
    value: 'actions',
    description: 'Adds the main launch buttons back to the hero panel.',
  },
  {
    id: 'feature-status-grid',
    label: 'Status Cards',
    kind: 'feature',
    rarity: 'epic',
    weight: 7,
    value: 'status-grid',
    description: 'Unlocks the launch status cards under the hero.',
  },
  {
    id: 'feature-demo-panel',
    label: 'Signal Portal',
    kind: 'feature',
    rarity: 'legendary',
    weight: 5,
    value: 'demo-panel',
    description: 'Reveals the relay panel linking to demo.steves.tools.',
  },
  {
    id: 'feature-orbital-ring',
    label: 'Orbital Ring',
    kind: 'feature',
    rarity: 'epic',
    weight: 4,
    value: 'orbital-ring',
    description: 'Wraps the greeting in the reactive orbital treatment.',
  },
  {
    id: 'feature-eyebrow',
    label: 'Signal Beacon',
    kind: 'feature',
    rarity: 'common',
    weight: 3,
    value: 'eyebrow',
    description: 'Restores the steves.tools launchpad badge above the page.',
  },
  {
    id: 'feature-ai-guide',
    label: 'Steve’s Guide to Working with AI',
    kind: 'feature',
    rarity: 'legendary',
    weight: 2,
    value: 'ai-guide',
    description: 'Reveals Steve’s gamey, gem-gated rules for keeping AI-assisted work focused, tested, and repeatable.',
  },
  {
    id: 'gems-12',
    label: '12 Gems',
    kind: 'gems',
    rarity: 'common',
    weight: 22,
    value: 12,
    description: 'A pocketful of shiny launchpad currency.',
  },
  {
    id: 'gems-25',
    label: '25 Gems',
    kind: 'gems',
    rarity: 'common',
    weight: 18,
    value: 25,
    description: 'Enough gems to make the HUD sparkle.',
  },
  {
    id: 'font-mono',
    label: 'Terminal Font',
    kind: 'font',
    rarity: 'rare',
    weight: 13,
    value: 'font-mono',
    description: 'Turns the homepage into a hacker console.',
  },
  {
    id: 'headline-neon',
    label: 'Neon Greeting',
    kind: 'headline',
    rarity: 'rare',
    weight: 11,
    value: 'hello neon',
    description: 'Swaps the hero text for a brighter hello.',
  },
  {
    id: 'background-comets',
    label: 'Comet Veil',
    kind: 'background',
    rarity: 'epic',
    weight: 8,
    value: 'comets',
    description: 'Adds streaking loot-energy to the background.',
  },
  {
    id: 'fortune-cookie',
    label: 'Fortune Cookie',
    kind: 'fortune',
    rarity: 'epic',
    weight: 7,
    value: 'Your next prototype ships before the stars finish loading.',
    description: 'A crunchy prophecy from the static-site oracle.',
  },
  {
    id: 'font-arcade',
    label: 'Arcade Font',
    kind: 'font',
    rarity: 'rare',
    weight: 4,
    value: 'font-arcade',
    description: 'A bouncy, high-score inspired homepage title.',
  },
  {
    id: 'headline-chaos',
    label: 'Wild Spark',
    kind: 'headline',
    rarity: 'legendary',
    weight: 3,
    value: 'loot world',
    description: 'Rewrites the headline with suspicious confidence.',
  },
  {
    id: 'gems-60',
    label: '60 Gems',
    kind: 'gems',
    rarity: 'rare',
    weight: 5,
    value: 60,
    description: 'A dazzling hoard of sixty gems in one pull.',
  },
  {
    id: 'font-comic',
    label: 'Comic Font',
    kind: 'font',
    rarity: 'rare',
    weight: 6,
    value: 'font-comic',
    description: 'Renders the greeting in the internet’s most controversial font.',
  },
  {
    id: 'font-wizard',
    label: 'Wizard Font',
    kind: 'font',
    rarity: 'rare',
    weight: 6,
    value: 'font-wizard',
    description: 'An ancient spellbook script for distinguished greetings.',
  },
  {
    id: 'headline-uwu',
    label: 'Smol Transmission',
    kind: 'headline',
    rarity: 'rare',
    weight: 6,
    value: 'hewwo wowld',
    description: 'Makes the hero text unbearably adorable.',
  },
  {
    id: 'headline-leet',
    label: 'Leet Greeting',
    kind: 'headline',
    rarity: 'rare',
    weight: 5,
    value: 'h3ll0 w0rld',
    description: 'Rewrites the greeting for 1337 hackers only.',
  },
  {
    id: 'headline-shout',
    label: 'Maximum Enthusiasm',
    kind: 'headline',
    rarity: 'epic',
    weight: 4,
    value: 'hello world!!!',
    description: 'The greeting, but with maximum enthusiasm.',
  },
  {
    id: 'fortune-quantum',
    label: 'Quantum Fortune',
    kind: 'fortune',
    rarity: 'epic',
    weight: 4,
    value: 'The bug you are hunting is also hunting you. Bring snacks.',
    description: 'A prophecy entangled across every timeline at once.',
  },
  {
    id: 'fortune-gremlin',
    label: 'Gremlin Fortune',
    kind: 'fortune',
    rarity: 'epic',
    weight: 4,
    value: 'A tiny gremlin now lives in your codebase. It mostly helps.',
    description: 'A suspiciously mischievous prophecy from the loot gremlin.',
  },
  {
    id: 'background-warp',
    label: 'Warp Drive',
    kind: 'background',
    rarity: 'epic',
    weight: 5,
    value: 'warp',
    description: 'Streaks the starfield into faster-than-light warp trails.',
  },
  {
    id: 'background-disco',
    label: 'Disco Nebula',
    kind: 'background',
    rarity: 'legendary',
    weight: 3,
    value: 'disco',
    description: 'Sends the aurora cycling through the whole spectrum.',
  },
  {
    id: 'feature-cursor-trail',
    label: 'Stardust Cursor',
    kind: 'feature',
    rarity: 'epic',
    weight: 5,
    value: 'cursor-trail',
    description: 'Attaches a glittering stardust trail to your pointer.',
  },
  {
    id: 'feature-ufo-pet',
    label: 'Pocket UFO',
    kind: 'feature',
    rarity: 'legendary',
    weight: 3,
    value: 'ufo-pet',
    description: 'Adopts a tiny UFO that patrols the launchpad forever.',
  },
  {
    id: 'ak47',
    label: 'AK-47',
    kind: 'ak47',
    rarity: 'mythic',
    weight: 1,
    value: 'ak47',
    description: 'Equip it to perforate and crumble parts of the UI.',
  },
];

export const defaultInventory: LootInventory = {
  gems: 0,
  openedBoxCount: 0,
  ak47Unlocked: false,
  unlockedFeatureIds: [],
  unlockedFontIds: [],
  unlockedAiTipIds: [],
};

export function getTotalLootWeight(rewards: readonly LootReward[] = lootRewards): number {
  return rewards.reduce((total, reward) => total + reward.weight, 0);
}

export function pickLootReward(seed: number, rewards: readonly LootReward[] = lootRewards): LootReward {
  if (seed < 0 || seed >= 1) {
    throw new RangeError('Loot seed must be at least 0 and less than 1.');
  }

  const totalWeight = getTotalLootWeight(rewards);
  let cursor = seed * totalWeight;

  for (const reward of rewards) {
    cursor -= reward.weight;
    if (cursor < 0) {
      return reward;
    }
  }

  return rewards[rewards.length - 1];
}

export function getLootAnimationProfile(rarity: RewardRarity): LootAnimationProfile {
  const rarityRank: Record<RewardRarity, number> = {
    common: 0,
    rare: 1,
    epic: 2,
    legendary: 3,
    mythic: 4,
  };
  const rank = rarityRank[rarity];

  return {
    rarity,
    anticipationMs: 360 + rank * 55,
    burstMs: 460 + rank * 70,
    settleMs: 520 + rank * 80,
    chestLiftPx: 18 + rank * 6,
    chestShakeDegrees: 5 + rank * 1.25,
    burstScale: 1.08 + rank * 0.045,
  };
}


export function pickLootEasing(seed: number, easings: readonly LootEasingOption[] = lootEasingOptions): LootEasingOption {
  if (seed < 0 || seed >= 1) {
    throw new RangeError('Loot easing seed must be at least 0 and less than 1.');
  }

  if (easings.length === 0) {
    throw new RangeError('At least one loot easing option is required.');
  }

  return easings[Math.min(Math.floor(seed * easings.length), easings.length - 1)];
}

export function createLootSpinPlan(seed: number, currentRotationDegrees = 0, rewards: readonly LootReward[] = lootRewards, easingSeed = seed): LootSpinPlan {
  const reward = pickLootReward(seed, rewards);
  const easing = pickLootEasing(easingSeed);
  const landingIndex = rewards.findIndex((candidate) => candidate.id === reward.id);
  const segmentDegrees = 360 / rewards.length;
  const landingAngle = landingIndex * segmentDegrees + segmentDegrees / 2;
  const normalizedCurrentRotation = ((currentRotationDegrees % 360) + 360) % 360;
  const minimumTurns = 7;
  const finalRotationDegrees = currentRotationDegrees + minimumTurns * 360 + (360 - normalizedCurrentRotation) + landingAngle;

  const targetTickerIndex = rewards.length * 5 + landingIndex;
  const tickerItems = createTickerItems(0, rewards, 7);

  return {
    reward,
    easing,
    landingIndex,
    finalRotationDegrees,
    durationMs: 3000 + Math.round(seed * 900) + getLootAnimationProfile(reward.rarity).burstMs,
    tickerItems,
    tickerLabels: tickerItems.map((item) => item.reward.label),
    targetTickerIndex,
  };
}

export function createTickerItems(startIndex: number, rewards: readonly LootReward[] = lootRewards, repetitions = 7): LootTickerItem[] {
  const tickerLength = rewards.length * repetitions;

  return Array.from({ length: tickerLength }, (_, index) => ({
    reward: rewards[(startIndex + index) % rewards.length],
  }));
}

export function createTickerLabels(startIndex: number, rewards: readonly LootReward[] = lootRewards): string[] {
  return createTickerItems(startIndex, rewards).map((item) => item.reward.label);
}

export function getFeatureUnlockOptions(): FeatureOption[] {
  return featureUnlocks.map((feature) => ({ ...feature }));
}

export function getFontRewardOptions(rewards: readonly LootReward[] = lootRewards): FontOption[] {
  return rewards
    .filter((reward) => reward.kind === 'font')
    .map((reward) => ({
      id: String(reward.value),
      label: reward.label,
    }));
}

export function getUnlockedFeatureIds(inventory: LootInventory): string[] {
  return featureUnlocks
    .filter((feature) => hasUnlockedFeature(inventory, feature.id))
    .map((feature) => feature.id);
}

export function hasUnlockedFeature(inventory: LootInventory, featureId: string): boolean {
  if (featureId === 'color-wheel') {
    return inventory.unlockedFeatureIds.includes('color-wheel') && inventory.unlockedFeatureIds.includes('global-lighting');
  }

  return inventory.unlockedFeatureIds.includes(featureId);
}

function getUnlockedBackgroundIds(inventory: LootInventory): string[] {
  return Array.from(new Set([
    ...(inventory.unlockedBackgroundIds ?? []),
    ...(inventory.equippedBackgroundId ? [inventory.equippedBackgroundId] : []),
  ]));
}

function getUnlockedHeadlineIds(inventory: LootInventory): string[] {
  return Array.from(new Set([
    ...(inventory.unlockedHeadlineIds ?? []),
    ...(inventory.equippedHeadlineId ? [inventory.equippedHeadlineId] : []),
  ]));
}

export function isRewardUnlocked(inventory: LootInventory, reward: LootReward): boolean {
  switch (reward.kind) {
    case 'feature':
      return hasUnlockedFeature(inventory, String(reward.value));
    case 'font':
      return inventory.unlockedFontIds.includes(String(reward.value));
    case 'ak47':
      return inventory.ak47Unlocked;
    case 'background':
      return getUnlockedBackgroundIds(inventory).includes(String(reward.value));
    case 'headline':
      return getUnlockedHeadlineIds(inventory).includes(reward.id);
    case 'gems':
    case 'fortune':
      return false;
  }
}

export function createDuplicateGemReward(reward: LootReward): LootReward {
  const gemValue = duplicateGemPayoutByRarity[reward.rarity];

  return {
    id: `duplicate-${reward.id}-gems`,
    label: `+${gemValue} Gems`,
    kind: 'gems',
    rarity: reward.rarity,
    weight: reward.weight,
    value: gemValue,
    description: `${reward.label} is already unlocked, so this slot now pays out ${gemValue} gems instead.`,
  };
}

export function getEligibleLootRewards(inventory: LootInventory, rewards: readonly LootReward[] = lootRewards): LootReward[] {
  const hasGlobalLighting = hasUnlockedFeature(inventory, 'global-lighting');
  const canCollectGems = hasUnlockedFeature(inventory, 'gems');
  const hasBackgroundAnimation = hasUnlockedFeature(inventory, 'background-animation');
  const canDiscoverShop = hasUnlockedFeature(inventory, 'shop') || inventory.gems > 0;

  return rewards
    .filter((reward) => reward.id !== 'feature-color-wheel' || hasGlobalLighting)
    .filter((reward) => reward.id !== 'feature-shop' || canDiscoverShop)
    .filter((reward) => reward.kind !== 'gems' || canCollectGems)
    .filter((reward) => reward.kind !== 'background' || hasBackgroundAnimation)
    .map((reward) => (isRewardUnlocked(inventory, reward) && canCollectGems ? createDuplicateGemReward(reward) : reward))
    .filter((reward) => reward.kind !== 'gems' || canCollectGems);
}

export function getShopUnlockRewards(rewards: readonly LootReward[] = lootRewards): LootReward[] {
  return rewards.filter((reward) => ['feature', 'font', 'background', 'headline', 'ak47'].includes(reward.kind));
}

export function getShopItemCost(reward: LootReward): number {
  return shopCostByRarity[reward.rarity];
}

export function getShopItems(inventory: LootInventory, rewards: readonly LootReward[] = lootRewards): ShopItem[] {
  return getShopUnlockRewards(rewards).map((reward) => {
    const unlocked = isRewardUnlocked(inventory, reward);

    return {
      reward,
      cost: getShopItemCost(reward),
      unlocked,
      purchasable: !unlocked && !(reward.kind === 'feature' && reward.value === 'color-wheel' && !hasUnlockedFeature(inventory, 'global-lighting')) && inventory.gems >= getShopItemCost(reward),
    };
  });
}

export function purchaseShopReward(inventory: LootInventory, reward: LootReward): LootInventory {
  if (!getShopUnlockRewards().some((shopReward) => shopReward.id === reward.id)) {
    return inventory;
  }

  if (isRewardUnlocked(inventory, reward)) {
    return inventory;
  }

  if (reward.kind === 'feature' && reward.value === 'color-wheel' && !hasUnlockedFeature(inventory, 'global-lighting')) {
    return inventory;
  }

  const cost = getShopItemCost(reward);

  if (inventory.gems < cost) {
    return inventory;
  }

  return applyRewardToInventory({ ...inventory, gems: inventory.gems - cost }, reward);
}

export function isCollectionComplete(inventory: LootInventory, rewards: readonly LootReward[] = lootRewards): boolean {
  const progress = getCollectionProgress(inventory, rewards);

  return progress.total > 0 && progress.unlocked === progress.total;
}

export function getCollectionProgress(inventory: LootInventory, rewards: readonly LootReward[] = lootRewards): { unlocked: number; total: number } {
  const shopItems = getShopItems(inventory, rewards);

  return {
    unlocked: shopItems.filter((item) => item.unlocked).length,
    total: shopItems.length,
  };
}

export function getUnlockedMenuItems(inventory: LootInventory): UnlockMenuItem[] {
  const menuItems: UnlockMenuItem[] = [
    { id: 'home', label: 'Home', href: '#headline' },
  ];

  if (hasUnlockedFeature(inventory, 'demo-panel')) {
    menuItems.push({ id: 'demo-panel', label: 'Demo relay', href: 'https://demo.steves.tools', external: true });
  }

  if (hasUnlockedFeature(inventory, 'ai-guide')) {
    menuItems.push({ id: 'ai-guide', label: 'AI tips', href: '#ai-guide' });
  }

  return menuItems;
}

export function getUnlockedFontOptions(inventory: LootInventory, rewards: readonly LootReward[] = lootRewards): FontOption[] {
  const unlockedFontIds = new Set(inventory.unlockedFontIds);

  if (inventory.equippedFontId) {
    unlockedFontIds.add(inventory.equippedFontId);
  }

  const unlockedFonts = getFontRewardOptions(rewards).filter((font) => unlockedFontIds.has(font.id));

  return [defaultFontOption, ...unlockedFonts];
}

export function getLootComboBonus(openedBoxCount: number, rarity: RewardRarity): LootComboBonus {
  const streak = openedBoxCount % 5;

  if (openedBoxCount <= 0) {
    return { streak: 0, gems: 0, isJackpot: false };
  }

  if (streak !== 0) {
    return { streak, gems: 0, isJackpot: false };
  }

  return {
    streak: 5,
    gems: comboBonusByRarity[rarity],
    isJackpot: true,
  };
}

export function applyRewardToInventory(inventory: LootInventory, reward: LootReward): LootInventory {
  switch (reward.kind) {
    case 'feature': {
      const featureId = String(reward.value);

      if (featureId === 'color-wheel' && !hasUnlockedFeature(inventory, 'global-lighting')) {
        return inventory;
      }

      return {
        ...inventory,
        unlockedFeatureIds: Array.from(new Set([...inventory.unlockedFeatureIds, featureId])),
      };
    }
    case 'gems':
      return { ...inventory, gems: inventory.gems + Number(reward.value) };
    case 'ak47':
      return { ...inventory, ak47Unlocked: true };
    case 'font': {
      const fontId = String(reward.value);
      return {
        ...inventory,
        equippedFontId: fontId,
        unlockedFontIds: Array.from(new Set([...inventory.unlockedFontIds, fontId])),
      };
    }
    case 'background':
      if (!hasUnlockedFeature(inventory, 'background-animation')) {
        return inventory;
      }

      return {
        ...inventory,
        equippedBackgroundId: String(reward.value),
        unlockedBackgroundIds: Array.from(new Set([...getUnlockedBackgroundIds(inventory), String(reward.value)])),
      };
    case 'headline':
      return {
        ...inventory,
        equippedHeadlineId: reward.id,
        unlockedHeadlineIds: Array.from(new Set([...getUnlockedHeadlineIds(inventory), reward.id])),
      };
    case 'fortune':
      return { ...inventory, lastFortune: String(reward.value) };
  }
}

export function shouldGrantAkFromSearch(search: string): boolean {
  return new URLSearchParams(search).get('ak') === '47';
}

export function applyDebugGemsFromSearch(inventory: LootInventory, search: string): LootInventory {
  const debugGemValue = Number(new URLSearchParams(search).get('gems'));

  if (debugGemValue !== 10000) {
    return inventory;
  }

  return {
    ...inventory,
    gems: debugGemValue,
    unlockedFeatureIds: Array.from(new Set([...inventory.unlockedFeatureIds, 'gems', 'shop'])),
  };
}
